import { useState, type FormEvent } from 'react';
import { discordStartUrl, steamStartUrl, type IdentityDetail } from '../api/auth';
import { ca } from '../i18n/ca';
import { HelpMore } from './HelpMore';
import { ProviderButton } from './ProviderButton';
import { avatarAt } from './avatarAt';

type Provider = 'STEAM' | 'DISCORD';

/** An identity as the profile serves it: the provider's avatar comes with it when it is verified. */
type DetailWithAvatar = IdentityDetail & { avatarUrl?: string | null };

/**
 * The picture of a person, wherever the product shows one: the provider's avatar when
 * there is one, the initial of the name when there is not. Decorative on purpose: the
 * name it belongs to is always beside it, so it carries no alternative text of its own.
 *
 * An address the browser cannot load falls back to the initial as well. A provider is
 * free to move or withdraw a picture between the platform reading the address and
 * somebody opening the page, and a broken image where a face belongs looks worse than
 * the letter it replaced. Remembered by address, so a new one is tried afresh.
 */
export function Avatar({
  url,
  name,
  size = 40,
}: {
  url?: string | null;
  name: string;
  size?: number;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const box = { width: `${size}px`, height: `${size}px` };
  if (url && broken !== url) {
    return (
      <img
        className="avatar"
        src={avatarAt(url, size)}
        alt=""
        style={box}
        loading="lazy"
        onError={() => setBroken(url)}
      />
    );
  }
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <span
      className="avatar fallback"
      style={{ ...box, fontSize: `${Math.round(size * 0.42)}px` }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

interface Props {
  details: DetailWithAvatar[];
  steamEnabled: boolean;
  discordEnabled: boolean;
  /** Where the provider's round trip comes back to. */
  next: string;
  onClaim: (provider: Provider, subject: string) => Promise<void>;
  onRemove: (provider: Provider) => Promise<void>;
  notices?: { steam?: string | null; discord?: string | null };
}

/**
 * The person's outside accounts (UI doc 5.9), explained: what each one is
 * for, the two ways of linking it (the provider's button, which verifies;
 * typing it, which only claims) and where it stands now. Shared by the
 * profile and the first sign-in's welcome page.
 */
export function IdentityPanel({
  details,
  steamEnabled,
  discordEnabled,
  next,
  onClaim,
  onRemove,
  notices,
}: Props) {
  return (
    <div className="stack" aria-label={ca.identitiesTitle}>
      <HelpMore short={ca.identitiesIntro} more={ca.identitiesIntroMore} />
      <ProviderBlock
        provider="STEAM"
        title={ca.providers.STEAM}
        how={ca.steamHow}
        howMore={ca.steamHowMore}
        label={ca.steamIdLabel}
        hint={ca.steamIdHint}
        verifyLabel={ca.steamVerify}
        verifyUrl={steamEnabled ? steamStartUrl({ link: true, next }) : null}
        detail={details.find((d) => d.provider === 'STEAM') ?? null}
        notice={notices?.steam ? (ca.steamNotices[notices.steam] ?? null) : null}
        noticeOk={notices?.steam === 'linked'}
        onClaim={onClaim}
        onRemove={onRemove}
      />
      <ProviderBlock
        provider="DISCORD"
        title={ca.providers.DISCORD}
        how={ca.discordHow}
        howMore={ca.discordHowMore}
        label={ca.discordNameLabel}
        hint={ca.discordNameHint}
        verifyLabel={ca.discordVerify}
        verifyUrl={discordEnabled ? discordStartUrl({ link: true, next }) : null}
        detail={details.find((d) => d.provider === 'DISCORD') ?? null}
        notice={notices?.discord ? (ca.discordNotices[notices.discord] ?? null) : null}
        noticeOk={notices?.discord === 'linked'}
        onClaim={onClaim}
        onRemove={onRemove}
      />
    </div>
  );
}

function ProviderBlock({
  provider,
  title,
  how,
  howMore,
  label,
  hint,
  verifyLabel,
  verifyUrl,
  detail,
  notice,
  noticeOk,
  onClaim,
  onRemove,
}: {
  provider: Provider;
  title: string;
  how: string;
  howMore: string;
  label: string;
  hint: string;
  verifyLabel: string;
  verifyUrl: string | null;
  detail: DetailWithAvatar | null;
  notice: string | null;
  noticeOk: boolean;
  onClaim: (provider: Provider, subject: string) => Promise<void>;
  onRemove: (provider: Provider) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      await onClaim(provider, value.trim());
      setValue('');
    } finally {
      setBusy(false);
    }
  }

  // A verified identity is one line: nothing left to explain, nothing left to fill in.
  if (detail?.verified) {
    return (
      <section className="identity" aria-label={title}>
        {notice && (
          <p className={noticeOk ? 'hint' : 'error'} role="status">
            {notice}
          </p>
        )}
        <p className="small person">
          <Avatar
            url={detail.avatarUrl}
            name={detail.label ?? detail.playerName ?? title}
            size={40}
          />
          <span className="muted">{title}</span>
          <strong>
            {detail.label ? `@${detail.label}` : (detail.playerName ?? detail.subject)}
          </strong>
          <span className="chip on">{ca.identityVerifiedMark}</span>
          <button type="button" className="small" onClick={() => void onRemove(provider)}>
            {ca.claimRemove}
          </button>
        </p>
      </section>
    );
  }

  const status = !detail ? ca.identityNone : ca.identityClaimed;

  return (
    <section className="identity" aria-label={title}>
      <h3>
        {title} <span className={`chip ${detail?.verified ? 'on' : ''}`}>{status}</span>
      </h3>
      {notice && (
        <p className={noticeOk ? 'hint' : 'error'} role="status">
          {notice}
        </p>
      )}
      {detail && (
        <p className="small person">
          <Avatar
            url={detail.avatarUrl}
            name={detail.label ?? detail.playerName ?? title}
            size={40}
          />
          {detail.label && <strong>@{detail.label}</strong>} <code>{detail.subject}</code>
          {detail.playerName && (
            <span className="muted"> · {ca.identitySeenAs(detail.playerName)}</span>
          )}{' '}
          <button type="button" className="small" onClick={() => void onRemove(provider)}>
            {ca.claimRemove}
          </button>
        </p>
      )}
      {detail && !detail.verified && <p className="warn small">{ca.claimNoDoor}</p>}
      <HelpMore short={how} more={howMore} />
      <div className="row actions">
        {verifyUrl ? (
          <ProviderButton provider={provider} href={verifyUrl} label={verifyLabel} />
        ) : (
          <span className="muted small">{ca.doorClosedHint}</span>
        )}
      </div>
      <details className="identities">
        <summary className="muted small">{ca.claimFallback}</summary>
        <form className="form" onSubmit={submit}>
          <label>
            {label}
            <input
              name={`${provider.toLowerCase()}-subject`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={detail && !detail.verified ? detail.subject : ''}
              autoCapitalize="none"
            />
            <span className="hint">{hint}</span>
          </label>
          <div className="row actions">
            <button type="submit" disabled={busy || !value.trim()}>
              {ca.claimSave}
            </button>
          </div>
        </form>
      </details>
    </section>
  );
}
